import { DataManagementConfig } from '@shared/components/data-management';
import { AddInstituteAdminDialog } from '../components/AddInstituteAdminDialog';
import {
  InstituteAdminTableData,
  InstituteAdminFormData,
  RemoveInstituteAdminParams,
} from '../types';
import { useInstituteAdmins, useInviteInstituteAdmin } from '../hooks/useInstituteAdmins';
import { RoleChip, UserNameDisplay } from '@shared/components';
import { userInvitationApi } from '@features/user-invitation/api/userInvitationApi';
import { notificationService } from '@shared/components/notifications';

export const createInstituteAdminsConfig = (
  tenantId: string,
  domainId: string,
  removeInstituteAdminMutation?: {
    mutateAsync: (params: RemoveInstituteAdminParams) => Promise<void>;
  },
  _confirmFn?: (options: {
    title: string;
    message: string;
    confirmText: string;
    confirmButtonColor: 'warning' | 'error';
  }) => Promise<boolean>
): DataManagementConfig<
  InstituteAdminTableData,
  InstituteAdminFormData,
  InstituteAdminFormData
> => ({
  hooks: {
    useQuery: () => useInstituteAdmins(tenantId, domainId),
    useCreate: () => useInviteInstituteAdmin(tenantId, domainId),
    useDelete: removeInstituteAdminMutation ? () => removeInstituteAdminMutation : undefined,
  },

  table: {
    title: 'Institute Administrators',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      {
        key: 'role',
        label: 'Role',
        render: (role) =>
          role && typeof role === 'string' ? <RoleChip role={role} size="small" /> : null,
      },
      {
        key: 'assignedAt',
        label: 'Assigned At',
        render: (date) =>
          date && typeof date === 'string' ? new Date(date).toLocaleDateString() : null,
      },
      {
        key: 'assignedBy',
        label: 'Assigned By',
        render: (assignedBy) =>
          assignedBy && typeof assignedBy === 'string' ? (
            <UserNameDisplay userId={assignedBy} />
          ) : null,
      },
      {
        key: 'status',
        label: 'Status',
        render: (status) => {
          const color =
            status === 'active' ? '#4caf50' : status === 'invited' ? '#ff9800' : '#f44336';
          return (
            <span
              style={{
                color,
                fontWeight: 'bold',
                textTransform: 'capitalize',
              }}
            >
              {status}
            </span>
          );
        },
      },
    ],
    emptyMessage: 'No institute administrators found for this institute',
    errorMessage: 'Failed to load institute administrators. Please try refreshing the page.',
  },

  actions: {
    create: {
      label: 'Add Institute Admin',
      dialog: (props) => (
        <AddInstituteAdminDialog {...props} tenantId={tenantId} domainId={domainId} />
      ),
    },
    delete: removeInstituteAdminMutation
      ? {
          label: 'Remove',
          confirmTitle: 'Remove Institute Admin',
          confirmMessage: (admin) =>
            `Remove ${admin.name || admin.email} as institute administrator?`,
          transformParams: (admin) => ({
            userId: admin.userId,
            userRoleId: admin.userRoleId,
            userName: admin.name || admin.email,
          }),
        }
      : undefined,
    custom: [
      {
        label: 'Resend Invite',
        onClick: async (admin) => {
          try {
            const response = await userInvitationApi.resendInvitation(admin.email);
            if (response.data?.success) {
              notificationService.success(`Invitation resent to ${admin.email}`);
            } else {
              notificationService.error(
                response.data?.errorMessage || response.message || 'Failed to resend invitation'
              );
            }
          } catch (error) {
            console.error('Failed to resend invitation:', error);
            notificationService.error('Failed to resend invitation');
          }
        },
        color: 'primary',
        variant: 'text',
        condition: (admin) => admin.status === 'invited',
      },
    ],
  },

  features: {
    search: true,
    pagination: true,
  },
});
